import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../lib/supabase';
import { Loader2 } from 'lucide-react';
import { SKILLS } from '../constants/music';
import { useMusicTranslations } from '../utils/translations';
import { MusicianSearchSelect } from './filters/MusicianSearchSelect';
import type { Profile } from '../types/database';

const inviteMemberSchema = z.object({
  musician_id: z.string().uuid('Please select a musician').optional(),
  email: z.string().email('Please enter a valid email').optional(),
  instruments: z.array(z.string()).min(1, 'Please select at least one instrument/role'),
}).superRefine((data, ctx) => {
  if (!data.musician_id && !data.email) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please select a musician or enter an email address',
      path: ['musician_id'],
    }); 
  }
});

type InviteMemberFormData = z.infer<typeof inviteMemberSchema>;

interface InviteMemberFormProps {
  bandId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function InviteMemberForm({ bandId, onSuccess, onCancel }: InviteMemberFormProps) {
  const [musicians, setMusicians] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [selectedMusician, setSelectedMusician] = useState<Profile | null>(null);
  const { t } = useTranslation();
  const { getTranslatedSkills } = useMusicTranslations();

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<InviteMemberFormData>({
    resolver: zodResolver(inviteMemberSchema),
    defaultValues: {
      instruments: [],
    },
  });

  const watchInstruments = watch('instruments', []);
  const translatedSkills = getTranslatedSkills();

  useEffect(() => {
    const fetchMusicians = async () => {
      try {
        // First, get all profile IDs to exclude (current members and pending invitations)
        const [{ data: members }, { data: pendingInvites }] = await Promise.all([
          supabase
            .from('band_members')
            .select('profile_id')
            .eq('band_id', bandId),
          supabase
            .from('invitations')
            .select('musician_id')
            .eq('band_id', bandId)
            .eq('status', 'pending')
            .not('musician_id', 'is', null) // Only include non-null musician_ids
        ]);

        // Combine member IDs and pending invitation IDs
        const memberIds = members?.map(m => m.profile_id) || [];
        const pendingInviteIds = pendingInvites?.map(i => i.musician_id).filter(Boolean) || [];
        const excludeIds = [...memberIds, ...pendingInviteIds];

        // Get musicians who aren't already members and haven't been invited
        const query = supabase
          .from('profiles')
          .select('*')
          .eq('is_musician', true);

        // Only add the not-in clause if there are IDs to exclude
        if (excludeIds.length > 0) {
          query.not('id', 'in', `(${excludeIds.join(',')})`);
        }

        const { data: musicians, error } = await query;

        if (error) throw error;
        setMusicians(musicians || []);
      } catch (err) {
        console.error('Error fetching musicians:', err);
        setError('Failed to load musicians');
      } finally {
        setLoading(false);
      }
    };

    fetchMusicians();
  }, [bandId]);

  const onSubmit = async (data: InviteMemberFormData) => {
    if (submitting) return; // Prevent multiple submissions
    
    try {
      setSubmitting(true);
      setError('');

      if (data.musician_id) {
        // Invite existing musician
        const { error: inviteError } = await supabase
          .from('invitations')
          .insert({
            band_id: bandId,
            musician_id: data.musician_id,
            instruments: data.instruments,
          });

        if (inviteError) throw inviteError;
      } else if (data.email) {
        // Send email invitation
        const { data: invitationData, error: functionError } = await supabase.functions.invoke('send-invitation-email', {
          body: {
            email: data.email,
            bandId: bandId,
            instruments: data.instruments
          },
        });

        if (functionError) throw functionError;
        
        console.log('Invitation sent successfully:', invitationData);
      }

      onSuccess();
    } catch (err) {
      console.error('Error inviting member:', err);
      setError('Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMusicianSelect = (musician: Profile | null) => {
    setSelectedMusician(musician);
    setValue('musician_id', musician?.id || '');
  };

  const handleInstrumentToggle = (instrument: string) => {
    const currentInstruments = watchInstruments;
    const newInstruments = currentInstruments.includes(instrument)
      ? currentInstruments.filter(i => i !== instrument)
      : [...currentInstruments, instrument];
    setValue('instruments', newInstruments);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        <p className="mt-2 text-light/60">Loading musicians...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg text-center">
          {error}
        </div>
      )}

      {!showEmailInput ? (
        <>
          <div>
            <label className="block text-sm font-medium text-light/80 mb-2">
              {t('bands.inviteMember.searchMusicians')}
            </label>
            <MusicianSearchSelect
              musicians={musicians}
              selectedMusician={selectedMusician}
              onSelectMusician={handleMusicianSelect}
              placeholder={t('bands.inviteMember.searchPlaceholder')}
              error={errors.musician_id?.message}
              loading={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-light/80 mb-4">
              {t('bands.inviteMember.selectInstruments')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {translatedSkills.map((skill) => (
                <button
                  key={skill.value}
                  type="button"
                  onClick={() => handleInstrumentToggle(skill.value)}
                  className={`px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-all ${
                    watchInstruments.includes(skill.value)
                      ? 'bg-secondary text-light hover:bg-secondary/80'
                      : 'bg-[#232323] text-light/60 hover:bg-[#2a2a2a]'
                  }`}
                >
                  <span>{skill.label}</span>
                </button>
              ))}
            </div>
            {errors.instruments && (
              <p className="mt-2 text-sm text-red-500">{errors.instruments.message}</p>
            )}
          </div>

          <p className="text-center text-light/60">
            {t('bands.inviteMember.cantFind')}{' '}
            <button
              type="button"
              onClick={() => setShowEmailInput(true)}
              className="text-primary hover:text-primary/80 transition-colors"
            >
              {t('bands.inviteMember.sendEmail')}
            </button>
          </p>
        </>
      ) : (
        <>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-light/80 mb-2">
              {t('common.emailAddress')}
            </label>
            <input
              type="email"
              id="email"
              {...register('email')}
              placeholder={t('bands.inviteMember.enterEmail')}
              className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-light/80 mb-4">
              {t('bands.inviteMember.selectInstruments')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {translatedSkills.map((skill) => (
                <button
                  key={skill.value}
                  type="button"
                  onClick={() => handleInstrumentToggle(skill.value)}
                  className={`px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-all ${
                    watchInstruments.includes(skill.value)
                      ? 'bg-secondary text-light hover:bg-secondary/80'
                      : 'bg-[#232323] text-light/60 hover:bg-[#2a2a2a]'
                  }`}
                >
                  <span>{skill.label}</span>
                </button>
              ))}
            </div>
            {errors.instruments && (
              <p className="mt-2 text-sm text-red-500">{errors.instruments.message}</p>
            )}
          </div>

          <p className="text-center text-light/60">
            {t('bands.inviteMember.alreadyOnPlatform')}{' '}
            <button
              type="button"
              onClick={() => setShowEmailInput(false)}
              className="text-secondary hover:text-secondary/80 transition-colors"
            >
              {t('bands.inviteMember.searchExisting')}
            </button>
          </p>
        </>
      )}

      <div className="flex flex-col md:flex-row justify-end md:space-x-4 mt-8">
        <button
          type="button"
          onClick={onCancel}
          className="btn-outline order-2 mt-6 md:mt-0"
          disabled={submitting}
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="btn-secondary md:order-2"
          disabled={submitting}
        >
          {submitting ? (
            <span className="flex items-center space-x-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>{t('bands.inviteMember.sendingInvitation')}</span>
            </span>
          ) : (
            <>{t('bands.inviteMember.sendInvitation')}</>
          )}
        </button>
      </div>
    </form>
  );
}